/**
 * Self-healing ExHentai sessions.
 *
 * A saved `igneous` can go stale (copied long ago, issued to another
 * session, or saved before Bunko could fetch it itself). ExHentai then
 * answers every request with an empty body (Sad Panda), even though the
 * e-hentai login (`ipb_member_id` + `ipb_pass_hash`) is still valid.
 *
 * {@link createSessionHealer} wraps source methods so that a Sad Panda on a
 * logged-in auth blob triggers one forced `igneous` refresh followed by a
 * single retry. Refreshed cookies are kept in memory per login and applied to
 * later calls, and `onRefreshed` listeners persist them.
 */

import { SadPanda, SourceError } from '@/lib/errors';
import type { SourceAuth, SourceCookies } from '../types';

/** Don't re-refresh a login that still got Sad Panda right after a refresh. */
const REFRESH_COOLDOWN_MS = 2 * 60_000;

export type AcquireSession = (auth: SourceAuth) => Promise<{ cookies: SourceCookies }>;

function loginKey(auth: SourceAuth | undefined): string | undefined {
  const memberId = auth?.cookies?.ipb_member_id?.trim();
  const passHash = auth?.cookies?.ipb_pass_hash?.trim();
  if (auth?.anonymous || !memberId || !passHash) return undefined;
  return `${memberId}:${passHash}`;
}

export function createSessionHealer(acquire: AcquireSession) {
  const refreshed = new Map<string, SourceCookies>();
  const refreshedAt = new Map<string, number>();
  const inFlight = new Map<string, Promise<SourceCookies>>();
  const listeners = new Set<(auth: SourceAuth) => void>();

  /** Notified with the full auth blob after a refresh made a request succeed. */
  function onRefreshed(listener: (auth: SourceAuth) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  /** `auth` with any in-memory refreshed session cookies applied. */
  function apply(auth: SourceAuth | undefined): SourceAuth | undefined {
    const key = loginKey(auth);
    const cookies = key ? refreshed.get(key) : undefined;
    if (!auth || !cookies) return auth;
    return { ...auth, cookies: { ...auth.cookies, ...cookies } };
  }

  /**
   * The auth blob to persist when a refresh has replaced the saved session
   * cookies, or `undefined` when `auth` is already current.
   */
  function reconcile(auth: SourceAuth): SourceAuth | undefined {
    const next = apply(auth);
    if (!next || next === auth) return undefined;
    const changed = Object.entries(next.cookies ?? {}).some(
      ([name, value]) => auth.cookies?.[name] !== value,
    );
    return changed ? next : undefined;
  }

  async function refresh(auth: SourceAuth, key: string): Promise<SourceCookies> {
    const pending = inFlight.get(key);
    if (pending) return pending;
    const promise = (async () => {
      const cookies: SourceCookies = { ...auth.cookies };
      delete cookies.igneous;
      const result = await acquire({ ...auth, cookies });
      refreshed.set(key, result.cookies);
      refreshedAt.set(key, Date.now());
      return result.cookies;
    })().finally(() => inFlight.delete(key));
    inFlight.set(key, promise);
    return promise;
  }

  /** Wrap `fn` so a Sad Panda refreshes the session and retries once. */
  function heal<A extends unknown[], R>(
    fn: (...args: A) => Promise<R>,
    authIndex: number,
  ): (...args: A) => Promise<R> {
    return async (...args: A) => {
      const original = args[authIndex] as SourceAuth | undefined;
      const effective = apply(original);
      const callArgs = [...args] as A;
      callArgs[authIndex] = effective;
      try {
        return await fn(...callArgs);
      } catch (error) {
        const key = loginKey(effective);
        if (!(error instanceof SadPanda) || !key || !effective) throw error;
        const last = refreshedAt.get(key);
        if (last !== undefined && Date.now() - last < REFRESH_COOLDOWN_MS) {
          throw new SadPanda(
            'ExHentai still returned an empty page with a freshly issued igneous cookie. Check that exhentai.org shows galleries in your own browser.',
            { source: 'exhentai', cause: error },
          );
        }
        let cookies: SourceCookies;
        try {
          cookies = await refresh(effective, key);
        } catch (refreshError) {
          // The refresh explains the failure better (no access, logged out,
          // challenge, rate limit) than the generic empty-body Sad Panda.
          throw refreshError instanceof SourceError ? refreshError : error;
        }
        const healed: SourceAuth = { ...effective, cookies: { ...effective.cookies, ...cookies } };
        callArgs[authIndex] = healed;
        const result = await fn(...callArgs);
        for (const listener of listeners) listener(healed);
        return result;
      }
    };
  }

  return { apply, reconcile, heal, onRefreshed };
}
